import { cx } from '@emotion/css';
import { Box, Checkbox, Divider, Menu as MUIMenu } from '@mui/material';
import React, { memo, useCallback, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { ix } from '../../../utils/ix';
import { Button } from '../../atoms/Button';
import { SearchInput } from '../filterMenus/LightweightFilterMenu/components/SearchInput';
import { MAX_LIST_HEIGHT_PIXELS, MIN_LIST_WIDTH_PIXELS, OVERSCAN_COUNT, ROW_SIZE_PIXELS, SIGN_WIDTH_PIXELS, } from './AggregateDropdown.constants';
import useStyles from './AggregateDropdown.styles';
function sortOptions(options) {
    return options.sort((opt1, opt2) => {
        if (opt1.label > opt2.label)
            return 1;
        if (opt1.label < opt2.label)
            return -1;
        return 0;
    });
}
function filterOptions(options, filter) {
    return options.filter((option) => option.label.includes(filter));
}
function generateOptionValueFromString(label) {
    return label.replace(/ /g, '_').toLowerCase();
}
function optionExists(options, search) {
    return options.some((option) => Object.entries(search).every(([prop, value]) => option[prop] === value));
}
function AggregateDropdown({ allowCreation, className, defaultValues, generateNewOptionValue, groupSelected, hideSelectAll, immediateUpdate = true, id, label, noneAsAll, open, onChange, onDropdownClose, onDropdownOpen, options, renderButton, renderCustomControl, showSearch, size, values, }) {
    const anchorElementRef = useRef(null);
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState(options);
    const [selected, setSelected] = useState(Array.isArray(defaultValues) ? defaultValues : []);
    const [originallySelected, setOriginallySelected] = useState(null);
    const isControlled = open !== undefined || values !== undefined;
    const menuId = ix(id, 'menu');
    const isDropdownOpen = isControlled ? Boolean(open) : Boolean(anchorEl);
    const getValues = useCallback(() => {
        return isControlled ? values || [] : selected;
    }, [isControlled, selected, values]);
    const shouldShowCreateOption = () => {
        return allowCreation && search !== '' && !optionExists(items, { label: search });
    };
    const handleSelectAllClick = () => {
        const updated = [];
        if (getValues().length === 0)
            updated.push(...items);
        if (!isControlled)
            setSelected(updated);
        if (immediateUpdate)
            onChange === null || onChange === void 0 ? void 0 : onChange(updated);
    };
    const handleItemClick = (option) => {
        const selectedIndex = getValues().findIndex((o) => o.value === option.value);
        const updated = selectedIndex === -1
            ? sortOptions([...getValues(), option])
            : [...getValues().slice(0, selectedIndex), ...getValues().slice(selectedIndex + 1)];
        if (!isControlled)
            setSelected(updated);
        if (immediateUpdate)
            onChange === null || onChange === void 0 ? void 0 : onChange(updated);
    };
    const handleButtonClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        setOriginallySelected(getValues());
        onDropdownOpen === null || onDropdownOpen === void 0 ? void 0 : onDropdownOpen(event);
    }, [onDropdownOpen, getValues]);
    const handleSearchChange = useCallback((value) => {
        setSearch(value);
    }, []);
    const handleAddOption = () => {
        const newOption = generateNewOptionValue
            ? generateNewOptionValue(search)
            : {
                label: search,
                value: generateOptionValueFromString(search),
            };
        const updated = sortOptions([...items, newOption]);
        setItems(updated);
        setSearch('');
        if (immediateUpdate)
            onChange === null || onChange === void 0 ? void 0 : onChange(updated);
    };
    const handleClose = () => {
        setAnchorEl(null);
        onDropdownClose === null || onDropdownClose === void 0 ? void 0 : onDropdownClose();
        const selectedValues = getValues().map((o) => o.value);
        const optionsUnchanged = getValues().length === (originallySelected === null || originallySelected === void 0 ? void 0 : originallySelected.length) && originallySelected.every((o) => selectedValues.includes(o.value));
        if (!optionsUnchanged && !immediateUpdate) {
            onChange === null || onChange === void 0 ? void 0 : onChange(getValues());
            setOriginallySelected(getValues());
        }
    };
    const renderAggregateButtonComponent = () => {
        const currentItems = immediateUpdate ? getValues() : originallySelected || [];
        if (currentItems.length === (items === null || items === void 0 ? void 0 : items.length) || (noneAsAll && currentItems.length === 0)) {
            return (React.createElement("span", { id: ix(id, 'value'), className: classes.buttonContentAll }, "All"));
        }
        if (!noneAsAll && currentItems.length === 0) {
            return (React.createElement("span", { id: ix(id, 'value'), className: classes.buttonContentNone }, "None"));
        }
        const firstSelectedItem = sortOptions(currentItems)[0];
        return (React.createElement("span", { id: ix(id, 'value'), className: classes.buttonContentValues },
            React.createElement("span", null, firstSelectedItem === null || firstSelectedItem === void 0 ? void 0 : firstSelectedItem.label),
            React.createElement("span", null, currentItems.length > 1 ? ` +${currentItems.length - 1}` : '')));
    };
    const renderListItem = ({ data, index, style }) => {
        const option = data[index];
        if (!option) {
            return (React.createElement(Box, { style: style, key: index, className: classes.listDivider },
                React.createElement(Divider, null)));
        }
        return (React.createElement(Box, { id: ix(id, 'filter-option', option.value), key: index, style: style, className: classes.listItem, onClick: () => handleItemClick(option), role: "listitem" },
            React.createElement(Checkbox, { id: ix(id, 'filter-option-checkbox', option.value), className: classes.checkbox, checked: !!getValues().find((o) => o.value === option.value) }),
            React.createElement("span", { id: ix(id, 'filter-option-label', option.value), className: classes.checkboxLabel }, option.label)));
    };
    const renderList = () => {
        const width = Math.max(...items.map((o) => o.label.length * SIGN_WIDTH_PIXELS), MIN_LIST_WIDTH_PIXELS);
        const height = Math.min(ROW_SIZE_PIXELS * items.length, MAX_LIST_HEIGHT_PIXELS);
        const selectedValues = getValues().map((o) => o.value);
        const unselected = items.filter((item) => !selectedValues.includes(item.value));
        const unselectedFiltered = filterOptions(unselected, search);
        const currentItems = groupSelected
            ? [
                ...filterOptions(getValues(), search),
                ...(unselectedFiltered.length > 0 && selectedValues.length ? [null] : []),
                ...unselectedFiltered,
            ]
            : sortOptions(filterOptions(items, search));
        return (
        // @ts-ignore Until material ui and react-transition-group update their react deps
        React.createElement(FixedSizeList, { height: height, width: width, overscanCount: OVERSCAN_COUNT, itemData: currentItems, itemCount: currentItems.length, itemKey: (index, data) => { var _a; return ((_a = data[index]) === null || _a === void 0 ? void 0 : _a.value) || `divider-${index}`; }, itemSize: ROW_SIZE_PIXELS }, renderListItem));
    };
    return (React.createElement("div", { className: cx(className, classes.root), id: id },
        React.createElement("div", { ref: anchorElementRef },
            React.createElement(Button, { "aria-controls": menuId, "aria-haspopup": "true", className: cx(classes.button, {
                    [classes.activeButton]: isDropdownOpen,
                }), id: ix(id, 'chip'), onClick: handleButtonClick, size: size },
                React.createElement("div", { className: classes.buttonContainer, id: ix(id, 'button-content'), role: "menubar" }, renderButton ? (renderButton(getValues())) : (React.createElement(Box, { className: classes.buttonContent },
                    React.createElement("span", { id: ix(id, 'field-name'), className: classes.buttonContentLabel }, label),
                    renderAggregateButtonComponent()))))),
        React.createElement(MUIMenu, { classes: { paper: classes.menuPaper }, id: ix(id, 'menu'), anchorEl: isControlled && anchorElementRef ? anchorElementRef.current : anchorEl, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, open: isDropdownOpen, onClose: handleClose, keepMounted: true },
            React.createElement("div", { className: classes.menuContent, id: ix(id, 'menu-content') },
                React.createElement(Box, { id: ix(id, 'container'), className: classes.root },
                    showSearch && (React.createElement(SearchInput, { id: ix(id, 'search'), placeholder: label, value: search, onChange: handleSearchChange })),
                    (showSearch || renderCustomControl) && React.createElement(Divider, { className: classes.divider }), renderCustomControl === null || renderCustomControl === void 0 ? void 0 :
                    renderCustomControl(),
                    shouldShowCreateOption() && (React.createElement(Box, { id: ix(id, 'create'), className: classes.createOption, onClick: handleAddOption, role: "suggestion" },
                        "Create \u201C",
                        search,
                        "\u201D")),
                    !hideSelectAll && (React.createElement(Box, { id: ix(id, 'select-all'), className: classes.selectAll, onClick: handleSelectAllClick },
                        React.createElement(Checkbox, { id: ix(id, 'select-all-checkbox'), className: classes.checkbox, checked: getValues().length === items.length, indeterminate: getValues().length > 0 && getValues().length < items.length }),
                        React.createElement("label", { htmlFor: ix(id, 'select-all-checkbox') }, "All"))),
                    React.createElement(Box, { role: "list" }, renderList()))))));
}
export default memo(AggregateDropdown);
//# sourceMappingURL=AggregateDropdown.js.map